import {
  ANALYSIS_TYPES,
  INTX_TYPES,
  INTX_TYPE_TO_LABEL,
  NO_CURRENT_INTERSECTION,
} from "../../../constants/intxConstants";
import { AREA_TYPES } from "../../../constants/geographyConstants";
import {
  BASIC_CMF_INTERSECTIONS,
  COMPLEX_CMF_INTERSECTIONS,
  FACILITY_TYPE_TO_AREA_TYPE,
  SAFETY_INPUT_TYPES,
  SPF_INTERSECTIONS,
  USER_ENTERED_CMF_INTX_TYPES,
} from "./safetyConstants";
import { STEP_STORAGE_KEYS } from "../../../constants/storageConstants";
import { getAnalysisType } from "../../../util/dataSelectors";

/**
 * @param {object} projectData
 * @returns the project's area type, as opposed to the area type for a specific intersection alternative which can be rural, suburban, or urban. Global area type can only be urban or rural and is determined on the global safety inputs page
 */
export const getGlobalAreaType = (projectData) => {
  const globalSafetyData = projectData[STEP_STORAGE_KEYS.GLOBAL_SAFETY_DATA];
  const isInterchange =
    getAnalysisType(projectData) === ANALYSIS_TYPES.RAMP_TERMINAL_INTERSECTION;
  return isInterchange
    ? globalSafetyData.areaType
    : FACILITY_TYPE_TO_AREA_TYPE[globalSafetyData.safetyFacilityType];
};

/**
 * @param {string} currentIntx the currentIntersection field from step 2. It's the intersection type that exists at the site today
 * @param {string} intxType intersection alternative being analyzed
 * @returns one of SAFETY_INPUT_TYPES
 */
export const getSafetyAnalysisType = ({ currentIntx, intxType }) => {
  if (
    [...BASIC_CMF_INTERSECTIONS, ...COMPLEX_CMF_INTERSECTIONS].includes(
      intxType
    )
  ) {
    return SAFETY_INPUT_TYPES.CMF;
  }
  if (USER_ENTERED_CMF_INTX_TYPES.includes(intxType)) {
    return SAFETY_INPUT_TYPES.CMF_USER_ENTERED;
  }
  if (SPF_INTERSECTIONS.includes(intxType)) {
    return SAFETY_INPUT_TYPES.SPF;
  }

  if (intxType === INTX_TYPES.ALL_WAY_STOP) {
    if (
      currentIntx === NO_CURRENT_INTERSECTION ||
      currentIntx === INTX_TYPES.ALL_WAY_STOP
    ) {
      return SAFETY_INPUT_TYPES.SPF;
    }
    if (currentIntx === INTX_TYPES.TWSC) {
      return SAFETY_INPUT_TYPES.CMF;
    }
    if (currentIntx === INTX_TYPES.SIGNAL) {
      return SAFETY_INPUT_TYPES.SPF;
    }
  }
  if (intxType === INTX_TYPES.SIGNAL) {
    return currentIntx === INTX_TYPES.TWSC
      ? SAFETY_INPUT_TYPES.CMF
      : SAFETY_INPUT_TYPES.SPF;
  }
  if (intxType === INTX_TYPES.ONE_LANE_ROUNDABOUT) {
    if (
      currentIntx === NO_CURRENT_INTERSECTION ||
      currentIntx === INTX_TYPES.ALL_WAY_STOP
    ) {
      return SAFETY_INPUT_TYPES.SPF;
    }
    return SAFETY_INPUT_TYPES.CMF;
  }
  if (intxType === INTX_TYPES.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS) {
    return currentIntx === NO_CURRENT_INTERSECTION
      ? SAFETY_INPUT_TYPES.SPF
      : SAFETY_INPUT_TYPES.CMF;
  }
  throw new Error(
    `Could not find safety analysis type for intersection type ${intxType}`
  );
};

export const getCmfOverridesForIntersection = ({ intxType, projectData }) => {
  if (
    projectData[STEP_STORAGE_KEYS.CRASH_MODIFICATION_FACTORS]?.[intxType] ===
    undefined
  ) {
    return {};
  }
  const cmfWithNotes =
    projectData[STEP_STORAGE_KEYS.CRASH_MODIFICATION_FACTORS][intxType];
  return {
    fatalInjury: cmfWithNotes.fatalInjury.overrideValue,
    total: cmfWithNotes.total.overrideValue,
  };
};

/**
 *
 * @param {object} cmfOverrides contains fatalInjury and total CMF values
 * @param {object} fatalInjury contains value and helperText keys
 * @param {object} total contains value and helperText keys
 * @returns array of objects of length 2. One for fatalInjury and one for total
 */
export const getCmfsList = ({ cmfOverrides, fatalInjury, total }) => [
  {
    overrideValue: cmfOverrides.fatalInjury,
    type: "fatalInjury",
    ...fatalInjury,
  },
  { overrideValue: cmfOverrides.total, type: "total", ...total },
];

/**
 *
 * @param {string} areaType
 * @param {string} baseIntx user-entered intersection whose SPF we are basing the safety calculations off of. Can be either TWSC or SIGNAL
 * @returns array of objects of length 2. One for fatalInjury and one for total
 */
const getOneLaneRoundaboutCmfs = ({
  areaType = AREA_TYPES.RURAL,
  cmfOverrides,
  baseIntx,
}) => {
  const getCmfsListWithOverrides = (props) =>
    getCmfsList({ cmfOverrides, ...props });
  if (
    baseIntx === INTX_TYPES.SIGNAL ||
    baseIntx === INTX_TYPES.DIAMOND_WITH_SIGNALS
  ) {
    return getCmfsListWithOverrides({
      fatalInjury: { value: 0.45 },
      total: { value: 0.74 },
    });
  }
  if (
    baseIntx === INTX_TYPES.TWSC ||
    baseIntx === INTX_TYPES.DIAMOND_WITH_STOP_CONTROL
  ) {
    const AREA_TYPE_TO_CMF_LIST = {
      [AREA_TYPES.RURAL]: getCmfsListWithOverrides({
        fatalInjury: { value: 0.13 },
        total: { value: 0.29 },
      }),
      [AREA_TYPES.SUBURBAN]: getCmfsListWithOverrides({
        fatalInjury: { value: 0.22 },
        total: { value: 0.22 },
      }),
      [AREA_TYPES.URBAN]: getCmfsListWithOverrides({
        fatalInjury: { value: 0.22 },
        total: { value: 0.61 },
      }),
    };
    return AREA_TYPE_TO_CMF_LIST[areaType];
  }
  throw new Error(
    `No one lane roundabout CMF found for base intersection: ${baseIntx}`
  );
};

/**
 *
 * @param {string} areaType
 * @param {string} baseIntx user-entered intersection whose SPF we are basing the safety calculations off of. Can be either TWSC or SIGNAL
 * @returns array of objects of length 2. One for fatalInjury and one for total
 */
const getTwoLaneRoundaboutCmfs = ({
  areaType = AREA_TYPES.RURAL,
  cmfOverrides,
  baseIntx,
}) => {
  const getCmfsListWithOverrides = (props) =>
    getCmfsList({ cmfOverrides, ...props });
  if (
    baseIntx === INTX_TYPES.SIGNAL ||
    baseIntx === INTX_TYPES.DIAMOND_WITH_SIGNALS
  ) {
    return getCmfsListWithOverrides({
      fatalInjury: { value: 0.29 },
      total: { value: 0.81 },
    });
  }
  if (
    baseIntx === INTX_TYPES.TWSC ||
    baseIntx === INTX_TYPES.DIAMOND_WITH_STOP_CONTROL
  ) {
    const ruralHelperText =
      "No rural CMF or two-lane roundabout CMF available. CMF for all area types and all roundabout types used instead";
    const AREA_TYPE_TO_CMF_LIST = {
      [AREA_TYPES.RURAL]: getCmfsListWithOverrides({
        fatalInjury: { helperText: ruralHelperText, value: 0.18 },
        total: { helperText: ruralHelperText, value: 0.56 },
      }),
      [AREA_TYPES.SUBURBAN]: getCmfsListWithOverrides({
        fatalInjury: { value: 0.32 },
        total: { value: 0.81 },
      }),
      [AREA_TYPES.URBAN]: getCmfsListWithOverrides({
        fatalInjury: {
          helperText: "No urban CMF available. Suburban CMF used instead",
          value: 0.32,
        },
        total: { value: 0.88 },
      }),
    };
    return AREA_TYPE_TO_CMF_LIST[areaType];
  }
  throw new Error(
    `No Two lane roundabout CMF found for base intersection: ${baseIntx}`
  );
};

/**
 *
 * @param {object} cmfOverrides might contain a fatalInjury and total cmf as object keys
 * @param {number} speedLimit speed limit of the crossroad of a DDI
 */
const getDdiCmfs = ({ cmfOverrides, speedLimit }) => {
  const getCmfsListWithOverrides = ({ fatalInjury, total }) =>
    getCmfsList({
      cmfOverrides,
      fatalInjury: { value: fatalInjury },
      total: { value: total },
    });

  if (speedLimit <= 25) {
    return getCmfsListWithOverrides({ fatalInjury: 0.23, total: 0.23 });
  }
  if (speedLimit <= 30) {
    return getCmfsListWithOverrides({ fatalInjury: 0.32, total: 0.34 });
  }
  if (speedLimit <= 35) {
    return getCmfsListWithOverrides({ fatalInjury: 0.43, total: 0.49 });
  }
  if (speedLimit <= 40) {
    return getCmfsListWithOverrides({ fatalInjury: 0.59, total: 0.71 });
  }
  if (speedLimit > 40) {
    return getCmfsListWithOverrides({ fatalInjury: 0.8, total: 1.03 });
  }

  throw new Error(`No DDI CMF found for speed limit ${speedLimit}`);
};

/**
 *
 * @param {string} areaType AREA_TYPE.URBAN or AREA_TYPE.RURAL
 * @param {object} cmfOverrides might contain a fatalInjury and total cmf as object keys
 * @param {string} currentIntx INTX_TYPE.SIGNAL or INTX_TYPE.TWSC
 */
const getAwscCmfs = ({ areaType, cmfOverrides, currentIntx }) => {
  const getCmfsListWithOverrides = (props) =>
    getCmfsList({ cmfOverrides, ...props });
  if (currentIntx === INTX_TYPES.TWSC) {
    if (areaType === AREA_TYPES.RURAL) {
      return getCmfsListWithOverrides({
        fatalInjury: { value: 0.23 },
        total: { value: 0.32 },
      });
    }
    if (areaType === AREA_TYPES.URBAN) {
      return getCmfsListWithOverrides({
        fatalInjury: { value: 0.3 },
        total: { value: 0.3 },
      });
    }
  }
  throw new Error(
    `Cannot find CMF values for AWSC when current intersection is ${areaType} ${currentIntx}`
  );
};

/**
 *
 * @param {string} areaType AREA_TYPE.URBAN or AREA_TYPE.RURAL
 * @param {object} cmfOverrides might contain a fatalInjury and total cmf as object keys
 * @param {object} currentIntx INTX_TYPE
 */
const getSignalCmfs = ({ areaType, cmfOverrides, currentIntx }) => {
  const getCmfsListWithOverrides = (props) =>
    getCmfsList({ cmfOverrides, ...props });
  if (currentIntx === INTX_TYPES.TWSC) {
    if (areaType === AREA_TYPES.RURAL) {
      return getCmfsListWithOverrides({
        fatalInjury: { value: 0.64 },
        total: { value: 0.64 },
      });
    }
    if (areaType === AREA_TYPES.URBAN) {
      return getCmfsListWithOverrides({
        fatalInjury: { value: 0.78 },
        total: { value: 0.84 },
      });
    }
  }
  throw new Error(
    `Cannot get CMF for signal with current intersection ${currentIntx} area type ${areaType}`
  );
};

/**
 * @param {string} [areaType] one of AREA_TYPES
 * @param {string} [baseIntx] Used only for roundabouts. User-entered intersection whose SPF we are basing the safety calculations off of. Can be either TWSC, SIGNAL, DIAMOND_WITH_SIGNALS, DIAMOND_WITH_STOP_CONTROL
 * @param {object} cmfOverrides contains total and fatalInjury numbers if someone entered an override on the defaults page
 * @param {string} currentIntx the currentIntersection field from step 2. It's the intersection type that exists at the site today
 * @param {string} intxType intersection alternative being analyzed
 * @param {number} [speedLimit] used for DDI intersections. Speed limit of the crossroad
 * @returns array of objects of length 2. One for fatalInjury and one for total
 */
export const getCmfsForIntxType = ({
  areaType,
  baseIntx,
  cmfOverrides,
  currentIntx,
  intxType,
  speedLimit,
}) => {
  const getValue = ({ default: defaultValue, twsc }) => {
    if (currentIntx === INTX_TYPES.TWSC && twsc) {
      return areaType === AREA_TYPES.URBAN ? twsc.urban : twsc.rural;
    }
    return defaultValue;
  };

  const getCmfsList = ({ fatalInjury, total }) => [
    {
      type: "fatalInjury",
      overrideValue: cmfOverrides.fatalInjury,
      value: getValue(fatalInjury),
    },
    {
      type: "total",
      overrideValue: cmfOverrides.total,
      value: getValue(total),
    },
  ];

  switch (intxType) {
    //
    // simple CMFs
    //
    case INTX_TYPES.DLT:
      return getCmfsList({
        fatalInjury: { default: 0.88, twsc: { urban: 0.75, rural: 0.64 } },
        total: { default: 0.88, twsc: { urban: 0.78, rural: 0.64 } },
      });
    case INTX_TYPES.MUT:
      return getCmfsList({
        fatalInjury: { default: 0.76, twsc: { urban: 0.67, rural: 0.63 } },
        total: { default: 0.63, twsc: { urban: 0.63, rural: 0.56 } },
      });
    case INTX_TYPES.RCUT_SIGNALIZED:
      return getCmfsList({
        fatalInjury: { default: 0.78, twsc: { urban: 0.68, rural: 0.64 } },
        total: { default: 0.85, twsc: { urban: 0.75, rural: 0.64 } },
      });
    case INTX_TYPES.RCUT_UNSIGNALIZED:
      return getCmfsList({
        fatalInjury: { default: 0.46 },
        total: { default: 0.65 },
      });
    case INTX_TYPES.CGT:
      return getCmfsList({
        fatalInjury: { default: 0.85, twsc: { urban: 0.73, rural: 0.64 } },
        total: { default: 0.96, twsc: { urban: 0.83, rural: 0.64 } },
      });
    case INTX_TYPES.JUGHANDLE:
      return getCmfsList({
        fatalInjury: { default: 0.74, twsc: { urban: 0.67, rural: 0.62 } },
        total: { default: 0.74, twsc: { urban: 0.7, rural: 0.67 } },
      });

    //
    // Complex CMFs
    //
    case INTX_TYPES.ONE_LANE_ROUNDABOUT:
    case INTX_TYPES.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS:
      return getOneLaneRoundaboutCmfs({ areaType, cmfOverrides, baseIntx });
    case INTX_TYPES.TWO_LANE_ROUNDABOUT:
    case INTX_TYPES.DIAMOND_WITH_TWO_LANE_ROUNDABOUTS:
      return getTwoLaneRoundaboutCmfs({ areaType, cmfOverrides, baseIntx });
    case INTX_TYPES.DDI:
      return getDdiCmfs({ cmfOverrides, speedLimit });
    case INTX_TYPES.ALL_WAY_STOP:
      return getAwscCmfs({ areaType, cmfOverrides, currentIntx });
    case INTX_TYPES.SIGNAL:
      return getSignalCmfs({ areaType, cmfOverrides, currentIntx });
    default:
      throw new Error(
        `Could not find CMF values for intersection type ${intxType}`
      );
  }
};

/**
 * @param {string} analysisType
 * @param {string} currentIntx
 * @param {string} intxType
 * @param {string[]} intxTypes list of intersection types the user has selected for analysis
 * @returns an array of objects with a value and label that is used for a select drop down.
 * This array becomes the options for which SPF types the CMFs can be applied to
 */
export const getSpfOptionsForCmfIntxType = ({
  analysisType,
  currentIntx,
  intxType,
  intxTypes,
}) => {
  const getOptionIfValid = (acc, type) => {
    if (
      (type === currentIntx || intxTypes.includes(type)) &&
      getSafetyAnalysisType({ currentIntx, intxType: type }) ===
        SAFETY_INPUT_TYPES.SPF
    ) {
      return [...acc, { value: type, label: INTX_TYPE_TO_LABEL[type] }];
    }
    return acc;
  };
  // get possible base SPF intersections. Throw error if no valid intersections exist
  const getOptions = (spfOptions) => {
    const options = spfOptions.reduce(getOptionIfValid, []);
    if (options.length === 0) {
      const errorMessage = `Error: Could not find intersection with SPF to base ${intxType} analysis off of. Make sure to include ${spfOptions.join(
        "(with SPF calculation), "
      )} in your analysis.`;
      alert(errorMessage);
      return [{ value: "ERROR", label: "No SPF intersection found" }];
    }
    return options;
  };
  switch (intxType) {
    case INTX_TYPES.ONE_LANE_ROUNDABOUT:
    case INTX_TYPES.TWO_LANE_ROUNDABOUT:
      return getOptions([INTX_TYPES.TWSC, INTX_TYPES.SIGNAL]);
    case INTX_TYPES.DIAMOND_WITH_ONE_LANE_ROUNDABOUTS:
    case INTX_TYPES.DIAMOND_WITH_TWO_LANE_ROUNDABOUTS:
      return getOptions([
        INTX_TYPES.DIAMOND_WITH_STOP_CONTROL,
        INTX_TYPES.DIAMOND_WITH_SIGNALS,
      ]);
    case INTX_TYPES.QUADRANT_ROADWAY:
      return getOptions([INTX_TYPES.SIGNAL]);
    case INTX_TYPES.OTHER:
      const options =
        analysisType === ANALYSIS_TYPES.AT_GRADE_INTERSECTION
          ? [INTX_TYPES.TWSC, INTX_TYPES.ALL_WAY_STOP, INTX_TYPES.SIGNAL]
          : [
              INTX_TYPES.DIAMOND_WITH_SIGNALS,
              INTX_TYPES.DIAMOND_WITH_STOP_CONTROL,
            ];
      return getOptions(options);
    case INTX_TYPES.SINGLE_POINT_DIAMOND:
      return getOptions([INTX_TYPES.DIAMOND_WITH_SIGNALS]);
    default:
      throw new Error(
        `Intersection type ${intxType} does not accept user provided CMFs`
      );
  }
};

/**
 * @param {object} projectData
 * @returns true if no CMF default fields have been saved
 * returns false if any value has been saved
 */
export const isSafetyDefaultsEmpty = (projectData) => {
  const intersectionCmfs = Object.values(
    projectData[STEP_STORAGE_KEYS.CRASH_MODIFICATION_FACTORS] ?? {}
  );
  return intersectionCmfs.every((formValues = {}) => {
    return (
      Object.values(formValues).filter((value) => value?.overrideValue)
        .length === 0
    );
  });
};
